import { useCallback, useRef } from "react"
import { Form, FormFeedback, Label, Button, Row, Col, Badge, Input } from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import {
  useCreateRtgsBankTemplate,
  useEditRtgsBankTemplate,
} from "hooks/useRtgsBankTemplate"

import { COORD_FIELDS, DEFAULT_COORDS } from "./rtgsTemplateFields"

const validationSchema = Yup.object({
  bank_name: Yup.string().trim().required("Bank name is required"),
  is_active: Yup.boolean(),
})

const RtgsBankTemplateAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } =
    useCreateRtgsBankTemplate()
  const { mutateAsync: editMutation, isPending: editLoading } =
    useEditRtgsBankTemplate()

  const fileRef = useRef(null)

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const formData = new FormData()
      formData.append("bank_name", values.bank_name.trim())
      formData.append("is_active", values.is_active ? "1" : "0")

      // New templates need a starting overlay_config (default positions). Edits
      // preserve the existing layout — positions are tuned on the visual editor.
      if (!isEdit) {
        COORD_FIELDS.forEach(({ key }) => {
          const c = DEFAULT_COORDS[key]
          formData.append(`overlay_config[${key}][left]`, c.left)
          formData.append(`overlay_config[${key}][top]`, c.top)
          formData.append(`overlay_config[${key}][font_size]`, c.font_size)
          formData.append(`overlay_config[${key}][letter_spacing]`, c.letter_spacing)
          formData.append(`overlay_config[${key}][bold]`, c.bold ? "1" : "0")
        })
      }

      if (fileRef.current?.files?.[0]) {
        formData.append("template_file", fileRef.current.files[0])
      }

      try {
        const action = isEdit
          ? editMutation({ id: editedData.id, data: formData })
          : createMutation(formData)

        const res = await action
        toast.success(
          res?.message ??
            `Bank template ${isEdit ? "updated" : "created"} successfully.`
        )
        resetForm()
        toggleModal()
      } catch {
        // axios interceptor handles error toast
      }
    },
    [isEdit, editedData, createMutation, editMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      bank_name: editedData?.bank_name ?? "",
      is_active: editedData?.is_active ?? true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const isLoading = createLoading || editLoading

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Bank Template" : "Add Bank Template"}
      footer={
        <div className="d-flex gap-2 justify-content-end w-100">
          <Button
            type="button"
            color="secondary"
            onClick={toggleModal}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="success"
            form="rtgs-bank-template-form"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : isEdit ? "Save Changes" : "Add Template"}
          </Button>
        </div>
      }
    >
      <Form id="rtgs-bank-template-form" onSubmit={formik.handleSubmit}>
        <Row>
          <Col md={8}>
            <div className="mb-3">
              <Label htmlFor="bank_name">
                Bank Name <span className="text-danger">*</span>
              </Label>
              <InputField
                name="bank_name"
                id="bank_name"
                value={formik.values.bank_name}
                onChange={formik.handleChange}
                invalid={formik.touched.bank_name && !!formik.errors.bank_name}
                placeholder="e.g. Pubali Bank PLC"
              />
              {formik.touched.bank_name && formik.errors.bank_name && (
                <FormFeedback type="invalid">
                  {formik.errors.bank_name}
                </FormFeedback>
              )}
            </div>
          </Col>
          <Col md={4} className="d-flex align-items-center pt-1">
            <Button
              type="button"
              color={formik.values.is_active ? "success" : "secondary"}
              outline={!formik.values.is_active}
              size="sm"
              onClick={() =>
                formik.setFieldValue("is_active", !formik.values.is_active)
              }
            >
              <i
                className={`bx ${
                  formik.values.is_active ? "bx-check-circle" : "bx-x-circle"
                } me-1`}
              />
              {formik.values.is_active ? "Active" : "Inactive"}
            </Button>
          </Col>
        </Row>

        <div className="mb-3">
          <Label htmlFor="template_file">PDF Template File</Label>
          {editedData?.template_path && (
            <div className="mb-1">
              <Badge color="info" className="me-2">
                Current file uploaded
              </Badge>
              <small className="text-muted">
                Upload a new file to replace it.
              </small>
            </div>
          )}
          <Input
            type="file"
            id="template_file"
            accept=".pdf"
            innerRef={fileRef}
          />
          <small className="text-muted">
            Max 10 MB. Leave empty to keep current file.
          </small>
        </div>

        <div className="alert alert-info mb-0 py-2">
          <i className="mdi mdi-information-outline me-1" />
          After saving and uploading a pad PDF, use the{" "}
          <strong>
            <i className="mdi mdi-target" /> Configure
          </strong>{" "}
          button on the list to place fields visually on the pad.
        </div>
      </Form>
    </ThemeModal>
  )
}

export default RtgsBankTemplateAddEdit
